/**
 * DecisionPackageView Component
 * 
 * Renders a DecisionPackage in HRM-style format for human review.
 * 
 * Core Principle: The AI structures, humans decide.
 * - No ranking or preference signals
 * - All options presented as equally valid
 * - Uncertainty prominently disclosed
 * - Clear human actions: APPROVE, MODIFY, REJECT
 */

import React, { useState } from 'react';
import {
    Shield,
    ChevronDown,
    ChevronUp,
    AlertCircle,
    HelpCircle,
    Layers,
    ListChecks,
    BarChart2,
    Users,
    Clock,
    FileQuestion,
    Check,
    X
} from 'lucide-react';

// ============================================================================
// TYPES (matching backend DecisionPackage)
// ============================================================================

interface ContextSummary {
    currentSituation: string;
    constraints: string[];
    stakeholders: string[];
    timeSensitivity: string;
    missingInformation: string[];
}

interface SubDecision {
    question: string;
    explanation: string;
    considerations: string[];
}

interface DecisionOption {
    id: string;
    description: string;
    pros: string[];
    cons: string[];
    risks: string[];
    respectsScope: boolean;
    respectsPolicy: boolean;
}

interface UncertaintyDisclosure {
    confidenceScore: number;
    assumptions: string[];
    knownUnknowns: string[];
    dataGaps: string[];
    confidenceStatement: string;
}

interface DecisionPackage {
    version: string;
    generatedAt: string;
    escalationId: string;
    decisionStatement: string;
    contextSummary: ContextSummary;
    subDecisions: SubDecision[];
    options: DecisionOption[];
    uncertainty: UncertaintyDisclosure;
    actionClass: 'A' | 'B' | 'C';
    actionClassReason: string;
    humanActions: string[];
}

// ============================================================================
// COMPONENTS
// ============================================================================

interface DecisionPackageViewProps {
    pkg: DecisionPackage;
    selectedOption: string | null;
    onSelectOption: (optionId: string) => void;
}

const ActionClassBadge: React.FC<{ actionClass: 'A' | 'B' | 'C'; reason: string }> = ({ actionClass, reason }) => {
    const config = {
        'A': { bg: 'bg-green-500/10', text: 'text-green-500', border: 'border-green-500/20', label: 'Class A — Low Risk' },
        'B': { bg: 'bg-yellow-500/10', text: 'text-yellow-500', border: 'border-yellow-500/20', label: 'Class B — Medium Risk' },
        'C': { bg: 'bg-red-500/10', text: 'text-red-500', border: 'border-red-500/20', label: 'Class C — High Risk' },
    };
    const { bg, text, border, label } = config[actionClass];

    return (
        <div className={`${bg} ${border} border rounded-lg p-3`}>
            <div className={`font-medium ${text} mb-1`}>{label}</div>
            <p className="text-xs text-hig-text-secondary">{reason}</p>
        </div>
    );
};

const ConfidenceBar: React.FC<{ score: number }> = ({ score }) => {
    const percentage = Math.round(score * 100);
    const color = score >= 0.7 ? 'bg-green-500' : score >= 0.4 ? 'bg-yellow-500' : 'bg-red-500';

    return (
        <div className="space-y-1">
            <div className="flex justify-between text-xs">
                <span className="text-hig-text-secondary">Confidence</span>
                <span className="text-hig-text-primary font-medium">{percentage}%</span>
            </div>
            <div className="h-2 bg-hig-bg-tertiary rounded-full overflow-hidden">
                <div
                    className={`h-full ${color} rounded-full transition-all`}
                    style={{ width: `${percentage}%` }}
                />
            </div>
        </div>
    );
};

const ExpandableSection: React.FC<{
    title: string;
    icon: React.ReactNode;
    children: React.ReactNode;
    defaultOpen?: boolean;
}> = ({ title, icon, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div className="border border-hig-border rounded-lg overflow-hidden">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between p-4 bg-hig-bg-tertiary hover:bg-hig-bg-tertiary/80 transition-colors"
            >
                <div className="flex items-center gap-2">
                    {icon}
                    <span className="font-medium text-hig-text-primary">{title}</span>
                </div>
                {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {isOpen && <div className="p-4 bg-hig-bg-secondary">{children}</div>}
        </div>
    );
};

const OptionCard: React.FC<{
    option: DecisionOption;
    isSelected: boolean;
    onSelect: () => void;
}> = ({ option, isSelected, onSelect }) => {
    return (
        <div
            onClick={onSelect}
            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${isSelected
                    ? 'border-hig-accent bg-hig-accent/5'
                    : 'border-hig-border hover:border-hig-accent/50'
                }`}
        >
            <div className="flex items-start justify-between mb-3">
                <p className="font-medium text-hig-text-primary">{option.description}</p>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-hig-accent bg-hig-accent' : 'border-hig-border'
                    }`}>
                    {isSelected && <Check size={12} className="text-white" />}
                </div>
            </div>

            <div className="grid grid-cols-3 gap-4 text-sm">
                {/* Pros */}
                <div>
                    <p className="text-xs text-green-500 font-medium mb-1">Pros</p>
                    <ul className="space-y-1">
                        {option.pros.map((pro, i) => (
                            <li key={i} className="text-hig-text-secondary text-xs flex items-start gap-1">
                                <Check size={10} className="text-green-500 mt-0.5 flex-shrink-0" />
                                {pro}
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Cons */}
                <div>
                    <p className="text-xs text-yellow-500 font-medium mb-1">Cons</p>
                    <ul className="space-y-1">
                        {option.cons.map((con, i) => (
                            <li key={i} className="text-hig-text-secondary text-xs flex items-start gap-1">
                                <AlertCircle size={10} className="text-yellow-500 mt-0.5 flex-shrink-0" />
                                {con}
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Risks */}
                <div>
                    <p className="text-xs text-red-500 font-medium mb-1">Risks</p>
                    <ul className="space-y-1">
                        {option.risks.map((risk, i) => (
                            <li key={i} className="text-hig-text-secondary text-xs flex items-start gap-1">
                                <X size={10} className="text-red-500 mt-0.5 flex-shrink-0" />
                                {risk}
                            </li>
                        ))}
                        {option.risks.length === 0 && (
                            <li className="text-hig-text-tertiary text-xs">No identified risks</li>
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const DecisionPackageView: React.FC<DecisionPackageViewProps> = ({
    pkg,
    selectedOption,
    onSelectOption,
}) => {
    return (
        <div className="space-y-6">
            {/* Decision Statement (The Core Question) */}
            <div className="bg-hig-bg-tertiary rounded-lg p-5 border-l-4 border-hig-accent">
                <div className="flex items-center gap-2 mb-2">
                    <HelpCircle size={18} className="text-hig-accent" />
                    <span className="text-sm font-medium text-hig-accent uppercase tracking-wide">
                        Decision Required
                    </span>
                </div>
                <p className="text-lg font-semibold text-hig-text-primary">
                    {pkg.decisionStatement}
                </p>
            </div>

            {/* Action Class + Confidence */}
            <div className="grid grid-cols-2 gap-4">
                <ActionClassBadge actionClass={pkg.actionClass} reason={pkg.actionClassReason} />
                <div className="bg-hig-bg-tertiary rounded-lg p-3">
                    <ConfidenceBar score={pkg.uncertainty.confidenceScore} />
                    <p className="text-xs text-hig-text-tertiary mt-2">
                        {pkg.uncertainty.confidenceStatement}
                    </p>
                </div>
            </div>

            {/* Context Summary */}
            <ExpandableSection
                title="Context & Facts"
                icon={<Layers size={16} className="text-hig-text-tertiary" />}
                defaultOpen={true}
            >
                <div className="space-y-4 text-sm">
                    <div>
                        <p className="text-hig-text-secondary">{pkg.contextSummary.currentSituation}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className="text-xs text-hig-text-tertiary mb-1 flex items-center gap-1">
                                <Users size={12} /> Stakeholders
                            </p>
                            <ul className="space-y-0.5">
                                {pkg.contextSummary.stakeholders.map((s, i) => (
                                    <li key={i} className="text-hig-text-secondary">{s}</li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <p className="text-xs text-hig-text-tertiary mb-1 flex items-center gap-1">
                                <Clock size={12} /> Time Sensitivity
                            </p>
                            <p className="text-hig-text-secondary">{pkg.contextSummary.timeSensitivity}</p>
                        </div>
                    </div>

                    {pkg.contextSummary.constraints.length > 0 && (
                        <div>
                            <p className="text-xs text-hig-text-tertiary mb-1">Constraints</p>
                            <ul className="list-disc list-inside space-y-0.5">
                                {pkg.contextSummary.constraints.map((c, i) => (
                                    <li key={i} className="text-hig-text-secondary">{c}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {pkg.contextSummary.missingInformation.length > 0 && (
                        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded p-3">
                            <p className="text-xs text-yellow-500 font-medium mb-1 flex items-center gap-1">
                                <FileQuestion size={12} /> Missing Information
                            </p>
                            <ul className="list-disc list-inside space-y-0.5">
                                {pkg.contextSummary.missingInformation.map((m, i) => (
                                    <li key={i} className="text-yellow-600 text-xs">{m}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </ExpandableSection>

            {/* Sub-Decisions */}
            {pkg.subDecisions.length > 0 && (
                <ExpandableSection
                    title={`Key Considerations (${pkg.subDecisions.length})`}
                    icon={<ListChecks size={16} className="text-hig-text-tertiary" />}
                    defaultOpen={false}
                >
                    <div className="space-y-4">
                        {pkg.subDecisions.map((sub, i) => (
                            <div key={i} className="border-l-2 border-hig-border pl-4">
                                <p className="font-medium text-hig-text-primary mb-1">{sub.question}</p>
                                <p className="text-sm text-hig-text-secondary mb-2">{sub.explanation}</p>
                                <ul className="list-disc list-inside space-y-0.5">
                                    {sub.considerations.map((c, j) => (
                                        <li key={j} className="text-xs text-hig-text-tertiary">{c}</li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </ExpandableSection>
            )}

            {/* Uncertainty Disclosure */}
            <ExpandableSection
                title="Uncertainty & Assumptions"
                icon={<BarChart2 size={16} className="text-hig-text-tertiary" />}
                defaultOpen={false}
            >
                <div className="space-y-4 text-sm">
                    {pkg.uncertainty.assumptions.length > 0 && (
                        <div>
                            <p className="text-xs text-hig-text-tertiary mb-1">Assumptions</p>
                            <ul className="list-disc list-inside space-y-0.5">
                                {pkg.uncertainty.assumptions.map((a, i) => (
                                    <li key={i} className="text-hig-text-secondary">{a}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {pkg.uncertainty.knownUnknowns.length > 0 && (
                        <div>
                            <p className="text-xs text-hig-text-tertiary mb-1">Known Unknowns</p>
                            <ul className="list-disc list-inside space-y-0.5">
                                {pkg.uncertainty.knownUnknowns.map((u, i) => (
                                    <li key={i} className="text-hig-text-secondary">{u}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {pkg.uncertainty.dataGaps.length > 0 && (
                        <div>
                            <p className="text-xs text-hig-text-tertiary mb-1">Data Gaps</p>
                            <ul className="list-disc list-inside space-y-0.5">
                                {pkg.uncertainty.dataGaps.map((g, i) => (
                                    <li key={i} className="text-hig-text-secondary">{g}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </ExpandableSection>

            {/* Options (NO Ranking, NO Preference) */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-hig-text-primary flex items-center gap-2">
                        <Shield size={16} className="text-hig-accent" />
                        Available Options
                    </h3>
                    <p className="text-xs text-hig-text-tertiary">
                        Select one to approve
                    </p>
                </div>
                <p className="text-xs text-hig-text-secondary mb-4 italic">
                    All options are presented as equally valid. The choice is yours.
                </p>
                <div className="space-y-3">
                    {pkg.options.map((option) => (
                        <OptionCard
                            key={option.id}
                            option={option}
                            isSelected={selectedOption === option.id}
                            onSelect={() => onSelectOption(option.id)}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default DecisionPackageView;
